import { useRef, useState } from "react";
import { useGame } from "@/game/GameContext";
import { WHEEL_OUTCOMES, rollWheel } from "@/game/gameData";
import PixelButton from "@/components/PixelButton";
import { sfx } from "@/game/audioEngine";

export default function BudgetWheelScreen() {
  const { state, dispatch } = useGame();
  const [angle, setAngle] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const totalSlices = WHEEL_OUTCOMES.length;
  const sliceAngle = 360 / totalSlices;
  const spinnerRef = useRef(null);

  const spin = () => {
    if (spinning) return;
    sfx.spin();
    setSpinning(true);
    setResult(null);
    const outcome = rollWheel();
    const idx = WHEEL_OUTCOMES.findIndex((o) => o.id === outcome.id);
    const targetCenter = idx * sliceAngle + sliceAngle / 2;
    const fullSpins = 5 + Math.floor(Math.random() * 3);
    // We want the pointer (top) to land at the targetCenter.
    // Rotation makes slice at angle X move to position -X relative to pointer.
    const finalAngle = fullSpins * 360 + (360 - targetCenter);
    setAngle(finalAngle);

    setTimeout(() => {
      setSpinning(false);
      setResult(outcome);
      dispatch({ type: "SET_WHEEL", outcome });
      sfx.ding();
    }, 4200);
  };

  // Build conic gradient
  const conic = WHEEL_OUTCOMES.map((o, i) => {
    const start = i * sliceAngle;
    const end = start + sliceAngle;
    return `${o.color} ${start}deg ${end}deg`;
  }).join(", ");

  return (
    <div className="min-h-screen px-4 py-8 flex flex-col items-center">
      <h2 className="pixel-font text-xl md:text-2xl text-[#F26522] text-center mb-2" data-testid="wheel-title">
        STARTUP BUDGET WHEEL
      </h2>
      <p className="vt323 text-xl text-white/80 text-center max-w-md">
        Spin the wheel to find out how much you have to start with.
      </p>

      <div className="relative my-8" style={{ width: "min(86vw, 380px)", height: "min(86vw, 380px)" }}>
        {/* Pointer */}
        <div className="absolute left-1/2 -translate-x-1/2 -top-2 z-20">
          <div className="wheel-pointer" />
        </div>

        {/* Wheel */}
        <div
          ref={spinnerRef}
          className="absolute inset-0 rounded-full border-8 border-black shadow-[8px_8px_0_0_#000] overflow-hidden"
          style={{
            background: `conic-gradient(${conic})`,
            transform: `rotate(${angle}deg)`,
            transition: spinning ? "transform 4s cubic-bezier(.16,.7,.16,1)" : "none",
          }}
          data-testid="budget-wheel"
        >
          {WHEEL_OUTCOMES.map((o, i) => {
            const rot = i * sliceAngle + sliceAngle / 2;
            const labelText =
              o.budget >= 1000
                ? `$${(o.budget / 1000).toFixed(o.budget % 1000 === 0 ? 0 : 1)}K`
                : `$${o.budget}`;
            return (
              <div
                key={o.id}
                className="absolute pointer-events-none"
                style={{
                  left: "50%",
                  top: "50%",
                  width: 0,
                  height: 0,
                  transform: `rotate(${rot}deg)`,
                }}
              >
                <span
                  className="pixel-font text-[15px] md:text-[20px] text-white whitespace-nowrap"
                  style={{
                    position: "absolute",
                    transform: `translate(-50%, calc(-1 * min(28vw, 118px))) rotate(${-rot}deg)`,
                    textShadow:
                      "2px 2px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000",
                  }}
                >
                  {labelText}
                </span>
              </div>
            );
          })}
          {/* Center hub with ASH phoenix */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full bg-black border-4 border-black grid place-items-center overflow-hidden">
            <img
              src="/brand/ash_logo_icon.png"
              alt="ASH"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>

      {!result && (
        <PixelButton data-testid="spin-wheel-button" onClick={spin} disabled={spinning}>
          {spinning ? "Spinning…" : "🎯 SPIN"}
        </PixelButton>
      )}

      {result && (
        <div className="mt-4 max-w-md text-center pixel-panel" data-testid="wheel-result">
          <div className="pixel-font text-sm text-[#F26522] mb-2">{result.label}</div>
          <div className="vt323 text-3xl mb-2">${result.budget.toLocaleString()}</div>
          <p className="text-sm">{result.message}</p>
          <div className="mt-4">
            <PixelButton
              data-testid="wheel-continue"
              onClick={() => dispatch({ type: "GOTO", screen: "supplies" })}
            >
              Shop Supplies ▶
            </PixelButton>
          </div>
        </div>
      )}

      {!result && (
        <p className="vt323 text-lg text-white/60 mt-6 max-w-md text-center">
          {state.wheelOutcome ? `Previous: $${state.wheelOutcome.budget.toLocaleString()}` : ""}
        </p>
      )}
    </div>
  );
}
